import { useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, ListGroup, Modal } from 'react-bootstrap';
import { FaBuilding, FaPlus, FaTrash } from 'react-icons/fa';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { CUSTOMER_API } from '../config';
import { LoadingButton } from './LoadingSpinner';
import SiteSelector from './SiteSelector';

function CustomerSiteList({
    customerId,
    sites = [],
    tenants = [],
    onUpdate,
    readOnly = false
}) {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
    const [pendingRemoveSiteId, setPendingRemoveSiteId] = useState(null);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [loadedSitesInfo, setLoadedSitesInfo] = useState({}); // Map of siteId -> siteName
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // Group sites by tenant - handle both id and tenant_id for backward compatibility
    const sitesByTenant = sites.reduce((acc, site) => {
        const tenantId = site.tenant_id || site.customer_tenant_id;
        if (!acc[tenantId]) {
            acc[tenantId] = {
                tenantName: site.tenant_name,
                sites: []
            };
        }
        acc[tenantId].sites.push(site);
        return acc;
    }, {});

    const handleAddSites = async () => {
        if (!selectedTenantId || selectedSiteIds.length === 0) {
            setError('Please select a tenant and at least one site');
            return;
        }

        setLoading(true);
        setError(null);

        // Check if customer is already associated with this tenant
        const isTenantAssociated = sites.some(s =>
            String(s.tenant_id) === String(selectedTenantId) ||
            String(s.customer_tenant_id) === String(selectedTenantId)
        );

        try {
            // FIX: If tenant is not associated, first create the association
            if (!isTenantAssociated) {
                const tenantAssociationResponse = await fetchWithAuth(CUSTOMER_API.ASSIGN_TENANTS(customerId), {
                    method: 'POST',
                    body: JSON.stringify({
                        tenant_ids: [selectedTenantId],
                        is_primary: false
                    })
                });

                if (!tenantAssociationResponse.ok) {
                    const errorText = await tenantAssociationResponse.text();
                    setError(`Failed to associate tenant: ${errorText}`);
                    setLoading(false);
                    return;
                }
                
                // Update local state with new tenant association
                const selectedTenant = tenants.find(t =>
                    String(t.TenantID || t.id || t.tenant_id) === String(selectedTenantId)
                );
                const tenantName = selectedTenant?.TenantName || selectedTenant?.name || selectedTenant?.tenant_name || 'Unknown Tenant';
                
                // Add placeholder sites to local state (will be replaced with real data after API)
                const placeholderSites = selectedSiteIds.map(siteId => ({
                    tenant_id: selectedTenantId,
                    customer_tenant_id: selectedTenantId,
                    site_id: siteId,
                    id: siteId,
                    tenant_site_id: siteId,
                    tenant_name: tenantName,
                    site_name: loadedSitesInfo[siteId] || `Site ${siteId}`,
                    status: 'active',
                    site_status: 'active'
                }));
                
                // Update local sites state to include the new tenant association
                const updatedSitesWithTenant = [...sites, ...placeholderSites];
                onUpdate && onUpdate(updatedSitesWithTenant);
            }

            // Now assign the sites (this should work now that tenant is associated)
            const response = await fetchWithAuth(CUSTOMER_API.ASSIGN_SITES(customerId), {
                method: 'POST',
                body: JSON.stringify({
                    tenant_id: selectedTenantId,
                    site_ids: selectedSiteIds
                })
            });

            if (response.ok) {
                // Get the tenant name from the selected tenant
                const selectedTenant = tenants.find(t =>
                    String(t.TenantID || t.id || t.tenant_id) === String(selectedTenantId)
                );
                const tenantName = selectedTenant?.TenantName || selectedTenant?.name || selectedTenant?.tenant_name || 'Unknown Tenant';

                // Build new site objects to add to the local list
                const newSites = selectedSiteIds.map(siteId => ({
                    tenant_id: selectedTenantId,
                    customer_tenant_id: selectedTenantId,
                    site_id: siteId,
                    id: siteId,
                    tenant_site_id: siteId,
                    tenant_name: tenantName,
                    // Get site name from loaded info or use placeholder
                    site_name: loadedSitesInfo[siteId] || `Site ${siteId}`,
                    // Default status for new site assignments
                    status: 'active',
                    site_status: 'active'
                }));

                // Update local list immediately for better UX
                const updatedSites = [...sites, ...newSites];
                onUpdate && onUpdate(updatedSites);
                
                setShowAddModal(false);
                setSelectedTenantId(null);
                setSelectedSiteIds([]);
                setError(null);
                setSuccess('Sites assigned successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                const errorText = await response.text();
                setError(`Failed to assign sites: ${errorText}`);
            }
        } catch (error) {
            console.error('Error assigning sites:', error);
            setError('Error assigning sites');
        } finally {
            setLoading(false);
        }
    };

    const isRemovingLastSite = sites.length === 1
        && pendingRemoveSiteId != null
        && sites.some(s => String(s.id || s.tenant_site_id) === String(pendingRemoveSiteId));

    const handleRemoveSiteClick = (tenantSiteId) => {
        setPendingRemoveSiteId(tenantSiteId);
        setShowRemoveConfirmModal(true);
    };

    const confirmRemoveSite = async () => {
        if (!pendingRemoveSiteId) return;

        setShowRemoveConfirmModal(false);
        setLoading(true);
        setError(null);

        try {
            // Use the id from flow_customer_tenant_sites, not tenant_site_id
            const response = await fetchWithAuth(CUSTOMER_API.REMOVE_SITE(customerId, pendingRemoveSiteId), {
                method: 'DELETE',
            });

            if (response.ok) {
                // Update local list immediately for better UX
                const updatedSites = sites.filter(s => s.id !== pendingRemoveSiteId);
                onUpdate && onUpdate(updatedSites);
                setError(null);
                setSuccess('Site removed successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                const errorText = await response.text();
                setError(`Failed to remove site: ${errorText}`);
            }
        } catch (error) {
            console.error('Error removing site:', error);
            setError('Error removing site');
        } finally {
            setLoading(false);
            setPendingRemoveSiteId(null);
        }
    };

    const cancelRemoveSite = () => {
        setShowRemoveConfirmModal(false);
        setPendingRemoveSiteId(null);
    };

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return <Badge variant="success">Active</Badge>;
            case 'INACTIVE':
                return <Badge variant="warning">Inactive</Badge>;
            case 'ENDED':
                return <Badge variant="secondary">Ended</Badge>;
            default:
                return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
        }
    };

    // Get existing tenant IDs to exclude from add list - handle both id/tenant_id (legacy) and TenantID (new)
    const existingTenantIds = sites.map(s => String(s.customer_tenant_id || s.tenant_id || s.id));
    const availableTenants = tenants.filter(t => {
        const tenantId = String(t.TenantID || t.id || t.tenant_id);
        return !existingTenantIds.includes(tenantId);
    });

    return (
        <div className="customer-site-list">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span><strong>Site Assignments</strong></span>
                    {!readOnly && tenants.length > 0 && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowAddModal(true)}
                        >
                            <FaPlus className="mr-1" /> Add Site
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {success && (
                        <Alert variant="success" className="mb-3">
                            {success}
                        </Alert>
                    )}
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {sites.length === 0 ? (
                        <div className="text-muted text-center py-3">
                            No site assignments found
                            {!readOnly && tenants.length > 0 && (
                                <div className="mt-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowAddModal(true)}
                                    >
                                        <FaPlus className="mr-1" /> Add First Site
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <ListGroup>
                            {sites.map(site => {
                                // Handle both id (new) and tenant_site_id (legacy) for backward compatibility
                                const siteId = site.id || site.tenant_site_id;
                                const siteStatus = site.status || site.site_status;
                                return (
                                <ListGroup.Item
                                    key={siteId}
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        <strong>{site.site_name}</strong>
                                        <div className="mt-1">
                                        </div>
                                        <small className="text-muted">{site.tenant_name}</small>
                                    </div>
                                    {!readOnly && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveSiteClick(siteId)}
                                            disabled={loading}
                                            title="Remove site"
                                        >
                                            <FaTrash />
                                        </Button>
                                    )}
                                </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {/* Add Site Modal */}
            <Modal show={showAddModal} onHide={() => {
                setShowAddModal(false);
                setSelectedTenantId(null);
                setSelectedSiteIds([]);
            }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Site Assignment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    <div className="mb-3">
                        <label className="font-weight-bold">Select Tenant</label>
                        <select
                            className="form-control"
                            value={selectedTenantId ?? ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Keep as string (UUID), don't parse to int
                                setSelectedTenantId(value || null);
                                setSelectedSiteIds([]);
                            }}
                            disabled={loading}
                        >
                            <option value="">Select a tenant...</option>
                            {tenants.map(tenant => {
                                // Handle both id/tenant_id (legacy) and TenantID (new) for backward compatibility
                                const tenantId = tenant.TenantID || tenant.id || tenant.tenant_id;
                                return (
                                <option key={tenantId} value={tenantId}>
                                    {tenant.TenantName || tenant.name || tenant.tenant_name || `Tenant ${tenantId}`}
                                </option>
                                );
                            })}
                        </select>
                    </div>

                    {selectedTenantId && (
                        <SiteSelector
                            tenantId={selectedTenantId}
                            selectedIds={selectedSiteIds}
                            onChange={setSelectedSiteIds}
                            multiple={true}
                            disabled={loading}
                            // Pass existing site IDs for this tenant to grey them out
                            existingSiteIds={sites
                                .filter(s => String(s.tenant_id || s.customer_tenant_id) === String(selectedTenantId))
                                .map(s => s.site_id || s.id || s.tenant_site_id)}
                            // Get site names when loaded
                            onSitesLoaded={(siteInfo) => {
                                const infoMap = {};
                                siteInfo.forEach(s => {
                                    infoMap[s.id] = s.name;
                                });
                                setLoadedSitesInfo(prev => ({...prev, ...infoMap}));
                            }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowAddModal(false);
                            setSelectedTenantId(null);
                            setSelectedSiteIds([]);
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="primary"
                        onClick={handleAddSites}
                        isLoading={loading}
                        loadingText="Adding..."
                        disabled={!selectedTenantId || selectedSiteIds.length === 0}
                    >
                        Add Sites
                    </LoadingButton>
                </Modal.Footer>
            </Modal>

            {/* Remove Site Confirmation Modal */}
            <Modal show={showRemoveConfirmModal} onHide={cancelRemoveSite} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isRemovingLastSite ? 'Remove last site?' : 'Confirm Remove Site'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isRemovingLastSite ? (
                        <>
                            <p className="mb-2">
                                This is the last site assigned to this service recipient.
                            </p>
                            <p className="mb-0 text-muted">
                                They will stay on the tenant, but appear under <strong>No site assigned</strong>
                                {' '}until you add a site again.
                            </p>
                        </>
                    ) : (
                        'Are you sure you want to remove this site? This action cannot be undone.'
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelRemoveSite}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmRemoveSite}>
                        {isRemovingLastSite ? 'Remove last site' : 'Remove Site'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CustomerSiteList;